/**
 * QR Codes Hub — landing page with 3 primary actions.
 * Screen code: QR-001
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { QrCode, PlusCircle, ListChecks, BarChart3 } from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import { useLanguage } from '../../context/LanguageContext';
import { usePerms } from '../../permissions/PermissionsContext';

export default function QRCodesHub() {
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const breadcrumb = [
    { labelAr: 'لوحة التحكم',    labelEn: 'Dashboard',      path: '/dashboard' },
    { labelAr: 'نظام QR Code',   labelEn: 'QR Code System'                    },
  ];

  const cards = [
    {
      key: 'new',
      titleAr: 'إنشاء QR Code جديد',
      titleEn: 'Create New QR Code',
      descAr: 'صمّم رمز جديد بألوان مخصصة وشعار في المنتصف ثم احفظه.',
      descEn: 'Design a new code with custom colors and optional center logo, then save.',
      icon: PlusCircle,
      color: '#d4af37',
      to: '/qr-codes/new',
      testid: 'qr-hub-new',
    },
    {
      key: 'manage',
      titleAr: 'متابعة والتحكم في الـ QR Code',
      titleEn: 'Manage QR Codes',
      descAr: 'قائمة كل الرموز التي أنشأتها — تعديل، تنزيل، حذف.',
      descEn: 'List of every code you created — edit, download, delete.',
      icon: ListChecks,
      color: '#10b981',
      to: '/qr-codes/manage',
      testid: 'qr-hub-manage',
    },
    {
      key: 'stats',
      titleAr: 'الإحصائيات',
      titleEn: 'Statistics',
      descAr: 'عدد مرات المسح، المواقع، الاتجاهات عبر الوقت.',
      descEn: 'Scan counts, locations, trends over time.',
      icon: BarChart3,
      color: '#3b82f6',
      to: '/qr-codes/stats',
      testid: 'qr-hub-stats',
    },
  ];

  return (
    <DashboardLayout
      title="نظام QR Code"
      titleEn="QR Code System"
      subtitle="إنشاء ومتابعة وإحصاء رموز QR لكل منصاتك"
      subtitleEn="Create, manage & analyze QR codes across all your platforms"
      screenCode="QR-001"
      backPath="/dashboard"
      onBack={() => navigate(-1)}
      breadcrumb={breadcrumb}
    >
      <section
        className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
        dir={isAr ? 'rtl' : 'ltr'}
        data-testid="qr-hub-root"
      >
        {/* Big banner icon */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10 flex flex-col items-center text-center"
        >
          <div className="w-20 h-20 rounded-2xl bg-[#d4af37]/15 border border-[#d4af37]/30 flex items-center justify-center mb-4">
            <QrCode className="w-10 h-10 text-[#d4af37]" />
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
            {t('اختر الإجراء', 'Choose an action')}
          </h2>
          <p className="text-white/50 text-sm max-w-md">
            {t(
              'كل رمز QR يُوَلَّد داخلياً بدون أي جهة خارجية أو تكلفة.',
              'Every QR code is generated in-house — no third-party dependency, no cost.',
            )}
          </p>
        </motion.div>

        {/* 3 action cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {visiblePages(cards, 'to').map((c, idx) => (
            <motion.button
              key={c.key}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 * idx }}
              whileHover={{ y: -6 }}
              onClick={() => navigate(c.to)}
              data-testid={c.testid}
              className="group relative text-center bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-8 cursor-pointer transition-all overflow-hidden hover:bg-white/[0.04]"
              style={{ direction: isAr ? 'rtl' : 'ltr' }}
            >
              {/* Hover glow */}
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500"
                style={{
                  background: `radial-gradient(circle at center, ${c.color}12 0%, transparent 70%)`,
                }}
              />
              <div
                className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{
                  boxShadow: `inset 0 0 0 1px ${c.color}60, 0 8px 32px ${c.color}20`,
                }}
              />

              <div className="relative">
                <div
                  className="w-16 h-16 mx-auto mb-5 rounded-2xl flex items-center justify-center border transition-transform group-hover:scale-110"
                  style={{
                    backgroundColor: `${c.color}18`,
                    borderColor: `${c.color}40`,
                  }}
                >
                  <c.icon className="w-8 h-8" style={{ color: c.color }} />
                </div>

                <h3 className="text-lg font-bold text-white mb-2">
                  {t(c.titleAr, c.titleEn)}
                </h3>
                <p className="text-xs text-white/50 leading-relaxed">
                  {t(c.descAr, c.descEn)}
                </p>
              </div>
            </motion.button>
          ))}
        </div>
      </section>
    </DashboardLayout>
  );
}
